/**
 * A site surveyed only to room level: comms rooms are recorded (from device
 * names and diagrams) but no rack has been surveyed, so there is nothing to
 * draw in 3D. Shows each room with the networks placed there, and each network
 * with the rooms that hold it — or says plainly that no room is recorded.
 */
import { cn } from '@pazportal/ui';
import {
  CONFIDENCE_LABEL,
  networkColor,
  roomNetworkIndex,
  type RackPlant,
  type RoomRow,
} from '../../lib/rackModel';

const ROLE_LABEL: Record<RoomRow['role'], string> = { mdf: 'MDF', mpoe: 'MPOE', idf: 'IDF', other: 'Room' };

export function PreSurveyPlant({ plant }: { plant: RackPlant }) {
  const byRoom = roomNetworkIndex(plant);
  const roomsOf = (networkId: string) =>
    plant.rooms
      .map((room) => ({ room, p: (byRoom.get(room.id) ?? []).find((x) => x.network.id === networkId) }))
      .filter((r): r is { room: RoomRow; p: NonNullable<typeof r.p> } => !!r.p);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="rounded-lg border border-dashed border-warning bg-warning-soft px-4 py-3 text-sm text-text">
        <span className="font-semibold">Pre-survey.</span>{' '}
        {plant.rooms.length} comms room{plant.rooms.length === 1 ? '' : 's'} recorded from device names and diagrams; no rack has been
        surveyed, so there are no elevations yet. Room placements are inferred — check the rack schedule's open items before relying on them.
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-2">
        <section className="min-h-0 overflow-auto rounded-lg border border-border bg-surface" aria-label="Comms rooms">
          <h2 className="sticky top-0 z-[1] border-b border-divider bg-surface px-4 py-3 text-sm font-bold text-text">
            Comms rooms ({plant.rooms.length})
          </h2>
          <ul className="divide-y divide-divider">
            {plant.rooms.map((room) => {
              const nets = byRoom.get(room.id) ?? [];
              return (
                <li key={room.id} className="px-4 py-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-semibold text-text">
                      {room.code}
                      {room.name ? ` · ${room.name}` : ''}
                    </span>
                    <span className="flex-none font-mono text-[10.5px] text-text-muted">
                      {ROLE_LABEL[room.role]}
                      {room.floor ? ` · floor ${room.floor}` : ''}
                    </span>
                  </div>
                  {room.note && <p className="mt-1 text-xs text-text-muted">{room.note}</p>}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {nets.length === 0 ? (
                      <span className="text-[11px] text-text-faint">No network placed here</span>
                    ) : (
                      nets.map((p) => (
                        <span
                          key={p.network.id}
                          title={p.note ?? undefined}
                          className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] text-text"
                        >
                          <span className="h-2 w-2 rounded-full" style={{ background: networkColor(p.network) }} />
                          {p.network.name}
                          {p.confidence !== 'verified' && (
                            <span className="text-[9.5px] uppercase tracking-wide text-text-muted">{CONFIDENCE_LABEL[p.confidence]}</span>
                          )}
                        </span>
                      ))
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="min-h-0 overflow-auto rounded-lg border border-border bg-surface" aria-label="Networks">
          <h2 className="sticky top-0 z-[1] border-b border-divider bg-surface px-4 py-3 text-sm font-bold text-text">
            Networks ({plant.networks.length})
          </h2>
          {plant.networks.length === 0 ? (
            <p className="p-4 text-sm text-text-muted">No networks recorded for this site.</p>
          ) : (
            <ul className="divide-y divide-divider">
              {plant.networks.map((n) => {
                const rooms = roomsOf(n.id);
                return (
                  <li key={n.id} className="px-4 py-3">
                    <div className="flex items-start gap-3">
                      <span className="mt-1 h-3 w-3 flex-none rounded-full" style={{ background: networkColor(n) }} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="truncate text-sm font-semibold text-text">{n.name}</span>
                          {n.owner && <span className="flex-none text-[11px] text-text-muted">{n.owner}</span>}
                        </div>
                        {n.description && <p className="mt-1 text-xs text-text-muted">{n.description}</p>}
                        <div className="mt-2 flex flex-wrap gap-1">
                          {rooms.length === 0 ? (
                            <span className="text-[11px] text-warning">No room recorded — locate on the survey</span>
                          ) : (
                            rooms.map(({ room, p }) => (
                              <span
                                key={room.id}
                                title={p.note ?? undefined}
                                className={cn(
                                  'rounded-sm border px-1.5 py-px font-mono text-[10.5px]',
                                  p.confidence === 'verified' ? 'border-border text-text' : 'border-dashed border-border text-text-muted',
                                )}
                              >
                                {room.code}
                                {p.confidence !== 'verified' ? ` · ${CONFIDENCE_LABEL[p.confidence].toLowerCase()}` : ''}
                              </span>
                            ))
                          )}
                        </div>
                        {n.source && <p className="mt-1.5 text-[10.5px] text-text-faint">Source: {n.source}</p>}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
